import { useNavigateToSearch } from '@components/SearchV2/hooks/useSearchQueryUrl';
import { useCurrency } from '@contexts/CurrencyContext';
import { useProductLists } from '@contexts/ProductListsContext';
import { analytics } from '@lib/analytics/page-events';
import { GENESIS_PROVIDER_ID } from '@lib/api/auth/genesis-provider';
import { buildCheckoutUrl as buildCheckoutUrlPure } from '@lib/commerce/buildCheckoutUrl';
import {
  CART_CHANGED_EVENT,
  notifyCartChanged,
  notifyCartRepriced,
  subscribeToOtherTabCartChanged,
} from '@lib/commerce/cartChanged';
import { hasUsableBridgeToken } from '@lib/commerce/ensureBridgeToken';
import { requestMiniCartOpen } from '@lib/commerce/miniCartOpen';
import { useEnsureBridgeToken } from '@lib/commerce/useBridgeToken';
import { useCartLines } from '@lib/commerce/useCartCount';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { useMultiCart } from '@lib/flags/useMultiCart';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import { failingFetcher } from '@lib/http/fetch';
import { FetchError } from '@lib/types/api/FetchError';
import { ReviewsData } from '@lib/types/api/Review';
import { buildManageSeatUrl, openManageSeatUrl } from '@lib/util/manageSeat';
import { buildRequestSeatUrl, openRequestSeatUrl } from '@lib/util/requestSeat';
import { openInUnityEditor } from '@lib/util/unityEditor';
import * as Sentry from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { REVIEWS_SHEET_INDEX } from '@ui-components/components/organisms/ProductTabs/ProductTabs';
import { LineItemType, getGetCartQueryKey } from '@unity/commerce-bridge';
import { useCartManagement } from '@unity/commerce-core';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ANALYTICS_POLLING_INTERVAL_MS,
  MAX_ANALYTICS_RETRIES,
} from 'src/app/constants/analytics';
import {
  PackageListDetailPropsData,
  ProductReviewFilterValues,
  ProductTabValue,
  ReviewFormData,
  WriteReviewModalPropsData,
  useBreakpoints,
} from 'ui-components';
import { formatDate } from 'utils';
import { MAX_REVIEW_STARS } from '../../constants/reviews';
import { useReviews } from '../../hooks/useReviews';
import { hasCartLine } from './hasCartLine';
import {
  CartToast,
  UpmProductDetailPagePropsActions,
  UpmProductDetailPagePropsData,
  UseUpmProductDetailPageProps,
} from './types';
import { useReleasesSort } from './useReleasesSort';

type CartFlow = 'Buy Now' | 'Add to cart';

// `cartError` is the cart error already showing when the write was armed, so a
// failure from an earlier attempt is not mistaken for this one's.
type PendingCartWrite = { quantity: number; cartError: unknown };

type CommerceCart = NonNullable<ReturnType<typeof useCartManagement>['cart']>;

// commerce-core types refetchCart as `() => void`, but it hands out React
// Query's refetch, which resolves to the query result; its data is `{ cart }`.
type CartRefetchResult = {
  isError?: boolean;
  error?: unknown;
  data?: { cart?: CommerceCart };
};

// An armed cart write that has neither a cart nor a cart error by now will
// not get one (the cart query is gated off, or a request hung): release the
// CTA instead of spinning forever. Covers React Query's three retries.
export const CART_LOAD_TIMEOUT_MS = 30_000;

export const useUpmProductDetailPage = (
  initialData: UpmProductDetailPagePropsData
): UseUpmProductDetailPageProps => {
  const router = useRouter();
  const breakpoints = useBreakpoints();
  const navigateToSearch = useNavigateToSearch();
  const { releasesSortBy, onReleasesSortChange } = useReleasesSort();

  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const { enabled: isMultiCartEnabled } = useMultiCart();
  const { hasCookieConsent, openCookieSettings } = useCookieSettings();
  const { isProductInList, toggleProductList, fetchProductMembership } =
    useProductLists();
  const { data: session } = useSession();
  const {
    cart,
    cartId,
    env,
    userId: cartUserId,
    organizationId,
    store,
    replaceLineItemsInCart,
    addLineItem,
    refetchCart,
    isError: isCartError,
    error: cartError,
    setEnableCartFetching,
    setCurrencyCode,
  } = useCartManagement();
  // The header badge's read, shared rather than repeated (same SWR entry).
  const { lines: cartLines } = useCartLines({ enabled: isMultiCartEnabled });
  const queryClient = useQueryClient();
  const { currency } = useCurrency();
  const ensureBridgeToken = useEnsureBridgeToken();

  // Keep the commerce cart's currency in sync with the footer/header selector;
  // the provider only reads currencyCode once at mount otherwise.
  useEffect(() => {
    setCurrencyCode(currency);
  }, [currency, setCurrencyCode]);
  const [data, setData] = useState(initialData);

  // The footer currency selector drives useCurrency(); selecting the displayed
  // price from initialData.priceByCurrency here keeps the PDP reactive on the
  // client, like search/listing/homepage.
  const displayedPrice =
    initialData.priceByCurrency?.[currency] ?? initialData.productInfo.price;
  const {
    currentPrice,
    originalPrice,
    currency: priceCurrency,
    discountPercent,
  } = displayedPrice;

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      productInfo: {
        ...prev.productInfo,
        price: {
          currentPrice,
          originalPrice,
          currency: priceCurrency,
          discountPercent,
        },
      },
    }));
  }, [currentPrice, originalPrice, priceCurrency, discountPercent]);

  const [shouldScrollToTab, setShouldScrollToTab] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  // tracked apart from showServerError, which a transient success clears
  const [entitlementErrorDismissed, setEntitlementErrorDismissed] =
    useState(false);
  // only when the failure actually collapsed the CTA — registry products keep theirs
  const showEntitlementError =
    Boolean(initialData.entitlementCheckFailed) &&
    initialData.productInfo.entitlement === 'preview' &&
    !initialData.previewMode &&
    !entitlementErrorDismissed;
  const [pendingPurchase, setPendingPurchase] =
    useState<PendingCartWrite | null>(null);
  // Multi-product cart: every cart write is an Add to cart append.
  // `knownCart` is the cart the page held at the click: if the append effect
  // still sees that same cart, it predates the click and is re-read first.
  const [pendingAppend, setPendingAppend] = useState<
    (PendingCartWrite & { knownCart: CommerceCart | undefined }) | null
  >(null);
  // The cart object an append last succeeded on, so the CTA reads View in cart
  // until commerce-core hands over a cart that reflects the append.
  const [appendedTo, setAppendedTo] = useState<object | null>(null);
  const [cartToast, setCartToast] = useState<CartToast | null>(null);
  const cartToastKey = useRef(0);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const hasTracked = useRef(false);
  const isTracking = useRef(false);

  const integrateReviewsData = useCallback(
    (reviewsData: ReviewsData | undefined) => {
      if (reviewsData?.reviews && Array.isArray(reviewsData.reviews)) {
        setData((prev) => {
          if (!prev.tabs?.reviews) return prev;

          const isMultiPackage = prev.productInfo.type === 'multi-package';

          const transformedReviews = reviewsData.reviews.map((review) => ({
            ...review,
            reviewDetails: {
              ...review.reviewDetails,
              canSubmitReview: true,
              ownedDate: prev.ownedDate || '',
              isMultiPackage,
              canReviewWithoutOwnership:
                prev.tabs.reviews?.canReviewWithoutOwnership,
              writeReviewModal: {
                ...(review.reviewDetails.writeReviewModal ||
                  prev.tabs?.reviews?.writeReviewModal ||
                  {}),
                productId: prev.productId,
                title: prev.tabs?.reviews?.writeReviewModal?.title || '',
                imageUrl: prev.tabs?.reviews?.writeReviewModal?.imageUrl || '',
                version:
                  review.reviewDetails.writeReviewModal?.version ||
                  prev.tabs?.reviews?.writeReviewModal?.version ||
                  '',
                ownedDate:
                  prev.tabs?.reviews?.writeReviewModal?.ownedDate ||
                  prev.ownedDate ||
                  '',
                isMultiPackage,
              } as WriteReviewModalPropsData,
            },
          }));

          return {
            ...prev,
            tabs: {
              ...prev.tabs,
              reviews: {
                ...prev.tabs.reviews,
                ...reviewsData,
                reviews: transformedReviews,
                activeFilterValue:
                  reviewsData.activeFilterValue as ProductReviewFilterValues,
                writeReviewModal: prev.tabs.reviews.writeReviewModal,
              },
            },
          };
        });
      }
    },
    []
  );

  const {
    reviewsData,
    onFilter: reviewsOnFilter,
    onSort: reviewsOnSort,
    onPageChange: reviewsOnPageChange,
    onSubmitReport: reviewsOnSubmitReport,
    onSubmitReview: reviewsOnSubmitReview,
    onDeleteReview: reviewsOnDeleteReview,
    onEditReview: reviewsOnEditReview,
    onEmailPublisher: reviewsOnEmailPublisher,
    onHelpfulVotesReview: reviewsOnHelpfulVotesReview,
    onSubmitReply: reviewsOnSubmitReply,
    onHelpfulVotesReply: reviewsOnHelpfulVotesReply,
    onEditReply: reviewsOnEditReply,
    onDeleteReply: reviewsOnDeleteReply,
    fetchReviewStats: reviewsFetchStats,
  } = useReviews({
    productId: data.productId,
    publisherSupportContact: data.nutritionLabels.publisher.supportContact,
    isMultiPackage: data.productInfo.type === 'multi-package',
    initialReviewsData: data.tabs?.reviews,
  });

  const fetchAndUpdateReviewStats = useCallback(
    async (productId: string) => {
      try {
        const stats = await reviewsFetchStats(productId);
        setData((prev) => {
          const reviewList = Object.entries(stats.ratingBreakdown).map(
            ([rating, count]) => ({
              value: Number(rating),
              review: count as number,
              max: MAX_REVIEW_STARS,
            })
          );

          const roundedRating = Math.round(stats.averageRating * 10) / 10;

          const latestFiveStarReview = prev.tabs.reviews?.reviews
            ?.filter(
              (review) => review.reviewDetails.stars.value === MAX_REVIEW_STARS
            )
            .sort(
              (a, b) =>
                new Date(b.reviewDetails.publishedDate).getTime() -
                new Date(a.reviewDetails.publishedDate).getTime()
            )[0];

          return {
            ...prev,
            productInfo: {
              ...prev.productInfo,
              reviews: prev.productInfo.reviews
                ? {
                    ...prev.productInfo.reviews,
                    value: roundedRating,
                    reviews: stats.totalReviews,
                  }
                : undefined,
            },
            nutritionLabels: {
              ...prev.nutritionLabels,
              latestUserReview: latestFiveStarReview
                ? {
                    ...prev.nutritionLabels.latestUserReview,
                    stars: {
                      value: latestFiveStarReview.reviewDetails.stars.value,
                      max: MAX_REVIEW_STARS,
                    },
                    gifted: latestFiveStarReview.reviewDetails.gifted ?? false,
                    review: latestFiveStarReview.reviewDetails.review,
                    title: latestFiveStarReview.reviewDetails.title,
                  }
                : undefined,
            },
            tabs: {
              ...prev.tabs,
              reviews: prev.tabs.reviews
                ? {
                    ...prev.tabs.reviews,
                    reviewStars: {
                      ...prev.tabs.reviews.reviewStars,
                      value: roundedRating,
                      reviews: stats.totalReviews,
                    },
                    reviewList,
                  }
                : undefined,
            },
          };
        });
      } catch (error) {
        console.error('Failed to update review stats:', error);
      }
    },
    [reviewsFetchStats]
  );

  useEffect(() => {
    if (reviewsData) {
      integrateReviewsData(reviewsData);
    }
  }, [reviewsData, integrateReviewsData]);

  const scrollToTab = useCallback(() => {
    const productTabs = document.getElementById('product-tabs');

    if (productTabs) {
      productTabs.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const getTabFromHash = useCallback((): ProductTabValue => {
    const hash = window.location.hash.replace('#', '');
    const validTabs: ProductTabValue[] = [
      'overview',
      'packages',
      'releases',
      'reviews',
    ];

    return validTabs.includes(hash as ProductTabValue)
      ? (hash as ProductTabValue)
      : 'overview';
  }, []);

  useEffect(() => {
    if (shouldScrollToTab) {
      const timeoutId = setTimeout(() => {
        scrollToTab();
        setShouldScrollToTab(false);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [data.currentTab, shouldScrollToTab, scrollToTab]);

  useEffect(() => {
    const initialHash = getTabFromHash();
    if (initialHash !== 'overview') {
      setData((prev) => ({ ...prev, currentTab: initialHash }));
      setShouldScrollToTab(true);
    }

    const handleHashChange = () => {
      const newTab = getTabFromHash();

      setData((prev) => ({ ...prev, currentTab: newTab }));
      setShouldScrollToTab(true);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [getTabFromHash]);

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      gallery: {
        ...prev.gallery,
        hasCookieConsent,
      },
    }));
  }, [hasCookieConsent]);

  // Extract variant to avoid complex dependency
  const firstPackageVersion =
    data.tabs.packages.packages[0]?.info.latestVersion;

  useEffect(() => {
    if (
      !hasTracked.current &&
      !isTracking.current &&
      !data.previewMode &&
      data.productId &&
      data.productInfo?.productTitle
    ) {
      isTracking.current = true;
      let analyticsRetryCount = 0;

      const checkAnalyticsAndTrack = () => {
        if (window.isAnalyticsInitialized) {
          const price =
            !data.productInfo.price ||
            Object.keys(data.productInfo.price).length === 0
              ? '0.00'
              : data.productInfo.price.currentPrice;

          analytics.pushProductDetailsEvent({
            brand: data.productInfo.publisherName,
            category: data.productInfo.type,
            id: data.productId,
            name: data.productInfo.productTitle,
            price,
            variant: firstPackageVersion,
          });

          analytics.pushProductsImpressionEvent([
            {
              brand: data.productInfo.publisherName,
              category: data.productInfo.type,
              id: data.productId,
              name: data.productInfo.productTitle,
              position: 1,
              price,
              variant: firstPackageVersion,
            },
          ]);

          hasTracked.current = true;
          isTracking.current = false;
        } else if (analyticsRetryCount < MAX_ANALYTICS_RETRIES) {
          analyticsRetryCount++;
          setTimeout(checkAnalyticsAndTrack, ANALYTICS_POLLING_INTERVAL_MS);
        } else {
          console.warn('Analytics did not initialize after maximum retries.');
          isTracking.current = false;
        }
      };

      checkAnalyticsAndTrack();
    }
  }, [
    data.previewMode,
    data.productId,
    data.productInfo?.productTitle,
    data.productInfo?.publisherName,
    data.productInfo?.type,
    data.productInfo?.price,
    firstPackageVersion,
  ]);

  useEffect(() => {
    if (isProductListsEnabled && !data.previewMode) {
      fetchProductMembership(data.productId);
    }
  }, [
    isProductListsEnabled,
    data.previewMode,
    data.productId,
    fetchProductMembership,
  ]);

  // These identifiers come from SSR via initialData and are immutable for the
  // lifetime of this hook. Reading them from initialData (instead of `data`)
  // avoids stale-closure footguns when `setData` replaces the data object from
  // unrelated updates (reviews, etc.).
  const {
    productSku,
    variantSku,
    subscriptionId,
    productVariantKey,
    entitledOrganizationId,
    entitlementDetails,
    productId,
    locale,
  } = initialData;

  // orgId is passed in (not closed over) so the caller can hand in the
  // guard-narrowed org: the outer organizationId is string | undefined, but
  // the tightened builder requires string.
  const buildCheckoutUrl = useCallback(
    (orgId: string) =>
      buildCheckoutUrlPure({ env, locale, organizationId: orgId, store }),
    [env, locale, store]
  );

  // Derived rather than stored, so a line removed elsewhere frees the CTA once
  // the cart is revalidated. A cart this page fetched or wrote is the freshest
  // source; until there is one, the header's read of the cart says it: that
  // read never creates a cart (`ensureExists=false`), so this survives a
  // reload without the page creating one on load. Flag off it reads nothing.
  const isInCart = cart
    ? hasCartLine(cart, variantSku) || appendedTo === cart
    : hasCartLine({ lineItems: cartLines }, variantSku);

  const failCartWrite = useCallback(() => {
    setShowServerError(true);
    setIsPurchasing(false);
  }, []);

  // Set while this page dispatches the cart-changed signal, so it does not
  // re-read the cart for its own append.
  const isNotifyingCartChange = useRef(false);

  // A line removed elsewhere in this tab (the header cart, say) or in another
  // tab must free the CTA, as it does the badge. Checkout is picked up by the
  // query's focus refetch. Only a cart this page already loaded is re-read: a
  // refetch would create one otherwise. Re-reading never signals a change, so
  // another tab's is not sent back out; its cart checks may re-price the cart
  // and bump its version, so an open mini-cart is told to reread once it lands.
  const hasLoadedCart = Boolean(cart?.id);
  useEffect(() => {
    if (!hasLoadedCart) return;
    const reread = async () => {
      await (
        refetchCart as unknown as () => Promise<CartRefetchResult | undefined>
      )();
      notifyCartRepriced();
    };
    const onCartChanged = () => {
      if (isNotifyingCartChange.current) return;
      void reread();
    };
    window.addEventListener(CART_CHANGED_EVENT, onCartChanged);
    const unsubscribeOtherTabs = subscribeToOtherTabCartChanged(() => {
      void reread();
    });
    return () => {
      window.removeEventListener(CART_CHANGED_EVENT, onCartChanged);
      unsubscribeOtherTabs();
    };
  }, [hasLoadedCart, refetchCart]);

  // `stage` names, in what it reports, the write the refresh is for.
  const refetchFreshCart = useCallback(
    async (stage: 'before append' | 'after append'): Promise<CommerceCart> => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const result = await Promise.race([
        (
          refetchCart as unknown as () => Promise<CartRefetchResult | undefined>
        )(),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error(`Cart refresh timed out ${stage}`));
            // Commerce Core forwards the query signal to the bridge request.
            void queryClient.cancelQueries({
              queryKey: getGetCartQueryKey(
                cartUserId ? `user:${cartUserId}` : (cartId ?? '')
              ),
              exact: true,
            });
          }, CART_LOAD_TIMEOUT_MS);
        }),
      ]).finally(() => clearTimeout(timeoutId));
      if (!result || result.isError) {
        throw (
          result?.error ?? new Error(`Cart could not be refreshed ${stage}`)
        );
      }
      const freshCart = result.data?.cart;
      if (!freshCart?.id) {
        throw new Error(`Cart refresh returned no cart ${stage}`);
      }
      return freshCart;
    },
    [refetchCart, queryClient, cartUserId, cartId]
  );

  // Buy now and Add to cart are only offered when the active org does not own
  // the asset, so every line they write is a fresh purchase. Checkout reads
  // lineItemType to handle can-subscribe errors correctly.
  const buildLineItem = useCallback(
    (flow: CartFlow, quantity: number) => {
      if (!variantSku || !productSku) {
        Sentry.captureException(
          new Error(`${flow} reached without a product or variant SKU`),
          { extra: { productId } }
        );
        return null;
      }
      return {
        productKey: productSku,
        variantKey: variantSku,
        quantity,
        lineItemType: LineItemType.purchase,
      };
    },
    [productSku, variantSku, productId]
  );

  // Checkout resolves the cart from organizationId; without it the redirect
  // lands on an org-less cart checkout cannot identify.
  const redirectToCheckout = useCallback(() => {
    if (!organizationId) {
      Sentry.captureException(
        new Error('organizationId missing on Buy Now checkout redirect'),
        { extra: { productId } }
      );
      failCartWrite();
      return;
    }
    window.location.assign(buildCheckoutUrl(organizationId));
  }, [organizationId, productId, buildCheckoutUrl, failCartWrite]);

  // A click armed a cart write, but fetching (or creating) the cart failed, so
  // neither write effect will ever run. Release the CTA and show the error; the
  // next click refetches. An error that was already there when the write was
  // armed is the previous attempt's, not this one's.
  useEffect(() => {
    const pending = pendingPurchase ?? pendingAppend;
    if (!pending || cart?.id || !isCartError) return;
    if (cartError === pending.cartError) return;
    Sentry.captureException(
      cartError ?? new Error('Cart could not be loaded for a cart write'),
      { extra: { productId } }
    );
    setPendingPurchase(null);
    setPendingAppend(null);
    failCartWrite();
  }, [
    pendingPurchase,
    pendingAppend,
    cart?.id,
    isCartError,
    cartError,
    productId,
    failCartWrite,
  ]);

  const isAwaitingCart = Boolean(pendingPurchase ?? pendingAppend) && !cart?.id;
  useEffect(() => {
    if (!isAwaitingCart) return;
    const timeoutId = setTimeout(() => {
      Sentry.captureException(new Error('Cart did not load for a cart write'), {
        extra: { productId },
      });
      setPendingPurchase(null);
      setPendingAppend(null);
      failCartWrite();
    }, CART_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isAwaitingCart, productId, failCartWrite]);

  useEffect(() => {
    if (!pendingPurchase || !cart?.id) return;
    setPendingPurchase(null);
    const lineItem = buildLineItem('Buy Now', pendingPurchase.quantity);
    if (!lineItem) {
      failCartWrite();
      return;
    }
    replaceLineItemsInCart([lineItem], {
      onSuccess: ({ success, cart: updatedCart }) => {
        const targetCartId = updatedCart?.id ?? cart.id;
        if (!success || !targetCartId) {
          Sentry.captureException(
            new Error('Cart replace did not return a cart on Buy Now'),
            { extra: { productId } }
          );
          failCartWrite();
          return;
        }
        redirectToCheckout();
      },
      onError: (error) => {
        Sentry.captureException(error);
        console.error('Failed to start purchase flow', error);
        failCartWrite();
      },
    });
  }, [
    pendingPurchase,
    cart?.id,
    productId,
    replaceLineItemsInCart,
    buildLineItem,
    redirectToCheckout,
    failCartWrite,
  ]);

  const showAlreadyInCartToast = useCallback(() => {
    cartToastKey.current += 1;
    setCartToast({ kind: 'already-in-cart', key: cartToastKey.current });
  }, []);

  // "Already in your cart" goes stale once a revalidated cart no longer holds
  // the line (removed in the mini-cart, say), so it goes with it.
  const wasInCart = useRef(isInCart);
  useEffect(() => {
    if (wasInCart.current && !isInCart) setCartToast(null);
    wasInCart.current = isInCart;
  }, [isInCart]);

  // Multi-product cart: append one line instead of replacing the cart. There is
  // one cart per user per store, so this is the same cart Buy Now would have
  // replaced; Add to cart then stays on the page. With the flag on no state
  // offers Buy now, so nothing goes to checkout from here.
  useEffect(() => {
    if (!pendingAppend || !cart?.id) return;
    setPendingAppend(null);
    const { quantity, knownCart } = pendingAppend;
    const flow = 'Add to cart';

    // The cart re-read that follows the append runs commerce-core's cart
    // checks, whose tax calculation bumps the cart version again. The drawer
    // reads the cart as it opens, so it opens only once that read settles: one
    // opened earlier held the pre-tax version, and its first remove or seat
    // change failed with a 412 it does not show.
    const settleAppend = async () => {
      let isSettled = true;
      try {
        await refetchFreshCart('after append');
      } catch (error) {
        Sentry.captureException(error, { extra: { productId } });
        isSettled = false;
      }
      isNotifyingCartChange.current = true;
      try {
        notifyCartChanged();
      } finally {
        isNotifyingCartChange.current = false;
      }
      setIsPurchasing(false);
      // As in asv2, the header mini-cart opens on the new line; the open
      // drawer is the confirmation, so no toast. The CTA was disabled while
      // the line was written, so closing it focuses the cart button. If the
      // cart could not be re-read, the drawer is left shut rather than opened
      // on a version that may already be stale; View in cart opens it.
      if (isSettled) requestMiniCartOpen({ returnFocus: 'cart-button' });
    };

    const append = async () => {
      // A cart loaded after the click is fresh; the one the page already held
      // may be stale (changed in another tab, or a failed attempt that did
      // land), so read it again before trusting it.
      let freshCart: CommerceCart = cart;
      if (cart === knownCart) {
        try {
          freshCart = await refetchFreshCart('before append');
        } catch (error) {
          Sentry.captureException(error, { extra: { productId } });
          failCartWrite();
          return;
        }
      }
      // Appending a variant that is already a line would bump its seats, so
      // the existing line is left alone and the user told it is already there.
      if (hasCartLine(freshCart, variantSku)) {
        setIsPurchasing(false);
        showAlreadyInCartToast();
        return;
      }
      const lineItem = buildLineItem(flow, quantity);
      if (!lineItem) {
        failCartWrite();
        return;
      }
      addLineItem(
        { lineItem },
        {
          // settleAppend re-reads the cart itself, and awaits it.
          refetch: false,
          onSuccess: ({ success }) => {
            if (!success) {
              Sentry.captureException(
                new Error(`Cart append did not succeed on ${flow}`),
                { extra: { productId } }
              );
              failCartWrite();
              return;
            }
            // The save-for-later list is deliberately left alone: a product
            // may be both saved and in the cart.
            setAppendedTo(freshCart);
            analytics.pushCartAddEvent({
              id: productId,
              name: data.productInfo.productTitle,
              price: data.productInfo.price.currentPrice,
              currency: data.productInfo.price.currency,
              brand: data.productInfo.publisherName,
              category: data.productInfo.type,
              variant: firstPackageVersion,
              quantity,
            });
            void settleAppend();
          },
          onError: (error) => {
            Sentry.captureException(error);
            console.error(`Failed to append to cart on ${flow}`, error);
            failCartWrite();
          },
        }
      );
    };
    void append();
  }, [
    pendingAppend,
    cart,
    variantSku,
    productId,
    addLineItem,
    refetchFreshCart,
    buildLineItem,
    failCartWrite,
    showAlreadyInCartToast,
    firstPackageVersion,
    data.productInfo.productTitle,
    data.productInfo.price.currentPrice,
    data.productInfo.price.currency,
    data.productInfo.publisherName,
    data.productInfo.type,
  ]);

  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;

  const redirectToSignIn = useCallback(async () => {
    const callbackUrl = window.location.pathname + window.location.search;
    await signIn(GENESIS_PROVIDER_ID, { callbackUrl });
  }, []);

  // View in cart (asv2's `viewInCart`): the header mini-cart shows the line.
  // Closing it signals a cart change, which re-reads the cart, so a line
  // removed there frees the CTA. The drawer shows the line, so an "already in
  // your cart" toast is dropped rather than left over it.
  const viewInCart = useCallback(() => {
    setCartToast(null);
    requestMiniCartOpen();
  }, []);

  // Both of these issue bridge calls — enabling the fetch, and arming the
  // effect that writes line items on an already-fetched cart — so neither may
  // happen before X-Authorization has something to carry.
  const startCartFlow = useCallback(
    (
      flow: CartFlow,
      armCartWrite: (pending: Pick<PendingCartWrite, 'cartError'>) => void
    ) => {
      setShowServerError(false);
      setIsPurchasing(true);
      ensureBridgeToken()
        .then((token) => {
          if (!token) {
            Sentry.captureException(
              new Error(`${flow}: no bridge token could be provisioned`),
              { extra: { productId } }
            );
            failCartWrite();
            return;
          }
          // CommerceCartProvider withholds the org id — and with it the cart —
          // until the signed-in user's own token is in place. A token that is
          // not that one (a guest token after a failed mint, say) would leave
          // the write waiting on a cart that never loads.
          if (!hasUsableBridgeToken(userId)) {
            Sentry.captureException(
              new Error(`${flow}: bridge token is not usable for the cart`),
              { extra: { productId } }
            );
            failCartWrite();
            return;
          }
          armCartWrite({ cartError });
          setEnableCartFetching(true);
          // Fetching is already on after a failed attempt, so retry it.
          if (isCartError && !cart?.id) void refetchCart();
        })
        .catch((error) => {
          Sentry.captureException(error, { extra: { productId } });
          failCartWrite();
        });
    },
    [
      setEnableCartFetching,
      ensureBridgeToken,
      refetchCart,
      isCartError,
      cart?.id,
      cartError,
      userId,
      productId,
      failCartWrite,
    ]
  );

  const purchaseProduct = useCallback(
    (quantity: number) => {
      analytics.pushBuyNowEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });
      if (!userId) {
        redirectToSignIn();
        return;
      }
      startCartFlow('Buy Now', (pending) =>
        setPendingPurchase({ ...pending, quantity })
      );
    },
    [
      userId,
      redirectToSignIn,
      startCartFlow,
      data.productId,
      data.productInfo.productTitle,
    ]
  );

  const addToCart = useCallback(
    (quantity: number) => {
      // A product the page already knows is a cart line is not appended again.
      if (isInCart) {
        viewInCart();
        return;
      }
      analytics.pushAddToCartEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });
      if (!userId) {
        redirectToSignIn();
        return;
      }
      startCartFlow('Add to cart', (pending) =>
        setPendingAppend({
          ...pending,
          quantity,
          knownCart: cart,
        })
      );
    },
    [
      isInCart,
      viewInCart,
      cart,
      userId,
      redirectToSignIn,
      startCartFlow,
      data.productId,
      data.productInfo.productTitle,
    ]
  );

  const applyEntitledState = useCallback(
    ({ justClaimed = true }: { justClaimed?: boolean } = {}) => {
      setData((prev) => ({
        ...prev,
        ...(justClaimed && {
          wasEntitled: true,
          ownedDate: formatDate(new Date(), 'PP', prev.locale),
        }),
        productInfo: {
          ...prev.productInfo,
          entitlement: 'entitled-current-org',
        },
        tabs: {
          ...prev.tabs,
          packages: {
            ...prev.tabs.packages,
            packages: prev.tabs.packages.packages.map((pck) => ({
              ...pck,
              isEntitled: true,
            })),
          },
        },
      }));
    },
    []
  );

  // A Back navigation can restore the page from bfcache with the CTA still
  // locked for a pending sign-in redirect; unlock it.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsClaiming(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const claimProduct = useCallback(async () => {
    if (isClaiming) {
      return;
    }
    if (!userId) {
      redirectToSignIn();
      return;
    }
    setShowServerError(false);
    setIsClaiming(true);
    let redirectingToSignIn = false;
    try {
      await failingFetcher('/api/claim-product', {
        method: 'POST',
        body: JSON.stringify({
          productId: data.productId,
        }),
      });

      analytics.pushAddToMyAssetEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });

      applyEntitledState();
    } catch (error) {
      if (error instanceof FetchError && error.status === 401) {
        // Keep the CTA disabled while the sign-in redirect is pending.
        redirectingToSignIn = true;
        redirectToSignIn().catch(() => setIsClaiming(false));
        return;
      }
      // 409 means the org already owns the product; show ownership, not an error.
      if (error instanceof FetchError && error.status === 409) {
        applyEntitledState({ justClaimed: false });
        return;
      }
      console.error('Error claiming product:', error);
      setShowServerError(true);
    } finally {
      if (!redirectingToSignIn) {
        setIsClaiming(false);
      }
    }
  }, [
    isClaiming,
    userId,
    redirectToSignIn,
    applyEntitledState,
    data.productId,
    data.productInfo.productTitle,
  ]);

  const updateCanSubmitReview = useCallback((canSubmitReview: boolean) => {
    setData((prev) => ({
      ...prev,
      canSubmitReview,
      purchasedItem: prev.purchasedItem
        ? {
            ...prev.purchasedItem,
            canWriteReview: canSubmitReview,
          }
        : prev.purchasedItem,
      tabs: {
        ...prev.tabs,
        reviews: prev.tabs.reviews
          ? {
              ...prev.tabs.reviews,
              canWriteReview: canSubmitReview,
            }
          : undefined,
      },
    }));
  }, []);

  const navigateToReviews = useCallback(() => {
    if (breakpoints?.isMobile) {
      setData((prev) => ({
        ...prev,
        activeSheetIndex: prev.tabs.reviews ? REVIEWS_SHEET_INDEX : null,
      }));
    } else {
      setData((prev) => ({
        ...prev,
        currentTab: 'reviews' as ProductTabValue,
      }));
    }

    setShouldScrollToTab(true);

    if (window.location.hash !== '#reviews') {
      window.location.hash = 'reviews';
    }
  }, [breakpoints?.isMobile]);

  const openPackageInUnity = useCallback(
    (upmPackage: PackageListDetailPropsData) => {
      analytics.pushOpenInUnityEvent({
        asset_id: upmPackage.id,
        asset_name: data.productInfo.productTitle,
      });
      openInUnityEditor({
        id: upmPackage.id,
        packageName: upmPackage.info.packageName,
        isSourceUpmRegistry: upmPackage.isSourceUpmRegistry,
      });
    },
    [data.productInfo.productTitle]
  );

  const openFirstPackageInUnity = useCallback(() => {
    openPackageInUnity(data.tabs.packages.packages[0]);
  }, [data.tabs.packages.packages, openPackageInUnity]);

  // The Organization Members page is org-scoped; without an org the dashboard
  // can't resolve the page, so report and skip rather than opening a broken
  // page (mirrors the Manage seats guard).
  const requestSeat = useCallback(() => {
    analytics.pushRequestSeatEvent({
      asset_id: data.productId,
      asset_name: data.productInfo.productTitle,
    });
    if (!organizationId) {
      Sentry.captureException(
        new Error('organizationId missing on Request seat redirect'),
        { extra: { productId: data.productId } }
      );
      return;
    }
    openRequestSeatUrl(buildRequestSeatUrl({ env, organizationId }));
  }, [env, organizationId, data.productId, data.productInfo.productTitle]);

  const actions: UpmProductDetailPagePropsActions = {
    onAddToMyAssets: claimProduct,
    // Buy now replaces the whole cart. Under the multi-product cart that would
    // drop every other line, and no flag-on state offers it (ProductPricing
    // shows Add to cart / View in cart instead), so it is not wired at all.
    onBuyNow: isMultiCartEnabled ? undefined : purchaseProduct,
    onAddToCart: addToCart,
    onViewInCart: viewInCart,
    onRequestSeat: requestSeat,
    onOpenInUnity: openFirstPackageInUnity,

    // Secondary CTA for paid owned assets. My Products is org-scoped; without an
    // org the dashboard can't resolve the page, so report and skip rather than
    // opening a useless home tab (mirrors the Buy Now checkout guard).
    onManageSeats: useCallback(() => {
      analytics.pushManageSeatsEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });
      if (!organizationId) {
        Sentry.captureException(
          new Error('organizationId missing on Manage seats redirect'),
          { extra: { productId: data.productId } }
        );
        return;
      }
      // The active org's own entitlement is the only one whose subscription its
      // org path can resolve, so take it from the per-org `entitlements`
      // expansion. Backends predating that expansion (and hard-failed
      // expansions) send only the singular one, which describes an arbitrary
      // entitled org — usable just when that org happens to be the active one.
      // With neither, the org-level list is the safe target: pairing one org's
      // path with another's subscription points at inventory it can't resolve.
      const activeOrgEntitlement = entitlementDetails?.find(
        (entitlement) => entitlement.organizationId === organizationId
      );
      const deepLink =
        activeOrgEntitlement ??
        (organizationId === entitledOrganizationId
          ? { subscriptionId, productVariantKey }
          : undefined);
      openManageSeatUrl(
        buildManageSeatUrl({
          env,
          organizationId,
          subscriptionId: deepLink?.subscriptionId,
          productVariantKey: deepLink?.productVariantKey,
        })
      );
    }, [
      env,
      organizationId,
      subscriptionId,
      productVariantKey,
      entitledOrganizationId,
      entitlementDetails,
      data.productId,
      data.productInfo.productTitle,
    ]),

    onOpenInUnityFromPackage: openPackageInUnity,

    onOpenInUnityFromEntitlement: openFirstPackageInUnity,

    onGoToMyAssets: useCallback(() => {
      router.push('/account/assets/upm');
    }, [router]),

    onGoToHomepage: useCallback(() => {
      router.push('/');
    }, [router]),

    onCloseFromEntitlement: useCallback(() => {
      setData((prev) => ({ ...prev, wasEntitled: false }));
    }, []),

    onSeeOwnedPackages: useCallback(() => {
      setData((prev) => ({ ...prev, currentTab: 'packages' }));
      setShouldScrollToTab(true);
    }, []),

    onPackageDetailsClick: useCallback(() => {
      setData((prev) => ({ ...prev, currentTab: 'packages' }));
      setShouldScrollToTab(true);
    }, []),

    onClickChipList: useCallback(
      (item?: string) => {
        if (!item) {
          return;
        }

        navigateToSearch(item);
      },
      [navigateToSearch]
    ),

    onHandleTabChange: useCallback((tab: ProductTabValue) => {
      setData((prev) => ({ ...prev, currentTab: tab }));

      if (tab !== 'reviews' && window.location.hash) {
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );
      }
    }, []),

    onOpenCookieSettings: openCookieSettings,

    onReleasesSortChange,

    onAccordionChange: useCallback((name: string, action: 'open' | 'close') => {
      analytics.pushProductAccordionClickEvent({
        action_name: name,
        action,
      });
    }, []),

    onDismiss: useCallback(() => {
      setShowServerError(false);
      setEntitlementErrorDismissed(true);
    }, []),

    onOpenSheet: useCallback((index: number) => {
      setData((prev) => ({ ...prev, activeSheetIndex: index }));
    }, []),
    onCloseSheet: useCallback(() => {
      setData((prev) => ({ ...prev, activeSheetIndex: null }));
    }, []),

    // Reviews actions
    onDeleteReview: useCallback(
      async (reviewId: string) => {
        try {
          await reviewsOnDeleteReview(reviewId);
          setShowServerError(false);
          await fetchAndUpdateReviewStats(data.productId);
          updateCanSubmitReview(true);
        } catch (error) {
          setShowServerError(true);
          throw error;
        }
      },
      [
        reviewsOnDeleteReview,
        fetchAndUpdateReviewStats,
        data.productId,
        updateCanSubmitReview,
      ]
    ),
    onFilterReviews: useCallback(
      async (filter: number) => {
        const filterStr = filter === 0 ? '' : filter.toString();
        setShouldScrollToTab(true);
        await reviewsOnFilter(filterStr);
      },
      [reviewsOnFilter]
    ),
    onEditReview: useCallback(
      async (
        reviewId: string,
        formData: ReviewFormData,
        lastUpdatedAt: string
      ) => {
        const updatedReviewsData = await reviewsOnEditReview(
          reviewId,
          formData,
          lastUpdatedAt
        );
        if (updatedReviewsData) {
          integrateReviewsData(updatedReviewsData);
          await fetchAndUpdateReviewStats(data.productId);
        }
      },
      [
        reviewsOnEditReview,
        integrateReviewsData,
        data.productId,
        fetchAndUpdateReviewStats,
      ]
    ),
    onFilter: useCallback(
      async (filter: string) => {
        setShouldScrollToTab(true);
        await reviewsOnFilter(filter);
      },
      [reviewsOnFilter]
    ),
    onSort: useCallback(
      async (sort: string) => {
        setShouldScrollToTab(true);
        await reviewsOnSort(sort);
      },
      [reviewsOnSort]
    ),
    onPageChange: useCallback(
      async (page: number) => {
        setShouldScrollToTab(true);
        const reviewsData = await reviewsOnPageChange(page);
        return reviewsData;
      },
      [reviewsOnPageChange]
    ),
    onSubmitReport: useCallback(
      async (
        reviewId: string,
        report: { reportReview: string; reportType: string }
      ) => {
        await reviewsOnSubmitReport(reviewId, data.productId, {
          reportReview: report.reportReview,
          type: report.reportType,
        });
      },
      [reviewsOnSubmitReport, data.productId]
    ),
    onEmailPublisher: reviewsOnEmailPublisher,
    onSubmitReview: useCallback(
      async (productId: string, formData: ReviewFormData) => {
        const reviewsData = await reviewsOnSubmitReview(productId, formData);
        if (reviewsData) {
          integrateReviewsData(reviewsData);
          await fetchAndUpdateReviewStats(productId);
          updateCanSubmitReview(false);
        }
      },
      [
        reviewsOnSubmitReview,
        integrateReviewsData,
        fetchAndUpdateReviewStats,
        updateCanSubmitReview,
      ]
    ),
    onClickReadMore: navigateToReviews,

    // TODO: These features are not going to be part of the GA launch
    onHelpfulVotesReview: reviewsOnHelpfulVotesReview,
    onSubmitReply: reviewsOnSubmitReply,
    onHelpfulVotesReply: reviewsOnHelpfulVotesReply,
    onEditReply: reviewsOnEditReply,
    onDeleteReply: reviewsOnDeleteReply,
    // Favorite actions
    onFavorite:
      isProductListsEnabled && !data.previewMode
        ? () => {
            toggleProductList({
              id: data.productId,
              name: data.productInfo.productTitle,
            });
          }
        : undefined,
  };

  // With the mini-cart, Save for later parks a product on its own list; the
  // bookmark reflects the shopper's lists only.
  const isFavorite =
    isProductListsEnabled && !data.previewMode
      ? isMultiCartEnabled
        ? isProductInList(data.productId, { excludeSavedForLater: true })
        : isProductInList(data.productId)
      : undefined;

  return {
    data: {
      ...data,
      productInfo: {
        ...data.productInfo,
        isFavorite,
        isPurchasing,
        isClaiming,
        // Flag off keeps Buy Now's replace-and-redirect untouched.
        isAddToCartEnabled: isMultiCartEnabled,
        isInCart,
      },
      tabs: {
        ...data.tabs,
        releases: { ...data.tabs.releases, sortBy: releasesSortBy },
      },
      showServerError: showServerError || showEntitlementError,
    },
    actions,
    cartToast,
    onDismissCartToast: useCallback(() => setCartToast(null), []),
  };
};
